import React, { useState } from 'react';
import { cameraApi } from '../../services/api';
import { useAppStore } from '../../store/appStore';

const PRESET_COLORS = ['#00d4ff','#00ff88','#ffaa00','#ff3344','#8855ff','#ff6b35','#00ccff','#66ff66'];

function GroupForm({ initial, onSave, onCancel, loading }) {
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [color, setColor] = useState(initial?.color || '#00d4ff');

  function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ name: name.trim(), description, color });
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: '12px' }}>
        <label className="label">Group Name *</label>
        <input className="input-field" value={name} onChange={e => setName(e.target.value)} autoFocus placeholder="e.g. Main Building" />
      </div>
      <div style={{ marginBottom: '12px' }}>
        <label className="label">Description</label>
        <input className="input-field" value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description" />
      </div>
      <div style={{ marginBottom: '16px' }}>
        <label className="label">Color</label>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
          {PRESET_COLORS.map(c => (
            <button key={c} type="button" onClick={() => setColor(c)} style={{
              width: '26px', height: '26px', borderRadius: '50%', background: c, cursor: 'pointer',
              border: color === c ? '2px solid #fff' : '2px solid transparent',
              boxShadow: color === c ? `0 0 8px ${c}` : 'none', transition: 'all 0.15s',
            }} />
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={loading || !name.trim()}>
          {loading ? 'Saving...' : initial ? 'Save Changes' : 'Create Group'}
        </button>
      </div>
    </form>
  );
}

export default function GroupsManagerModal({ onClose }) {
  const { groups, setGroups, addToast } = useAppStore();
  const [mode, setMode] = useState(null); // null | 'add' | { edit: group }
  const [actionLoading, setActionLoading] = useState(false);

  async function handleCreate(data) {
    setActionLoading(true);
    try {
      const res = await cameraApi.createGroup(data);
      setGroups([...groups, res.data]);
      addToast({ type: 'success', message: `Group "${data.name}" created` });
      setMode(null);
    } catch {
      addToast({ type: 'error', message: 'Failed to create group' });
    } finally { setActionLoading(false); }
  }

  async function handleEdit(group, data) {
    setActionLoading(true);
    try {
      const res = await cameraApi.updateGroup(group.id, data);
      setGroups(groups.map(g => g.id === group.id ? res.data : g));
      addToast({ type: 'success', message: `Group "${data.name}" updated` });
      setMode(null);
    } catch {
      addToast({ type: 'error', message: 'Failed to update group' });
    } finally { setActionLoading(false); }
  }

  async function handleDelete(group) {
    if (!window.confirm(`Delete group "${group.name}"?`)) return;
    setActionLoading(true);
    try {
      await cameraApi.deleteGroup(group.id);
      setGroups(groups.filter(g => g.id !== group.id));
      addToast({ type: 'success', message: `Group "${group.name}" deleted` });
    } catch {
      addToast({ type: 'error', message: 'Failed to delete group' });
    } finally { setActionLoading(false); }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: '480px' }}>
        <div className="modal-header">
          <h2>Manage Groups</h2>
          <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="modal-body">
          {mode === 'add' && (
            <div style={{ marginBottom: '16px', padding: '14px', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-bright)' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--accent-orange)', marginBottom: '12px' }}>New Group</div>
              <GroupForm onSave={handleCreate} onCancel={() => setMode(null)} loading={actionLoading} />
            </div>
          )}
          {mode?.edit && (
            <div style={{ marginBottom: '16px', padding: '14px', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-bright)' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--accent-orange)', marginBottom: '12px' }}>Edit Group</div>
              <GroupForm initial={mode.edit} onSave={data => handleEdit(mode.edit, data)} onCancel={() => setMode(null)} loading={actionLoading} />
            </div>
          )}

          {groups.length === 0 && !mode ? (
            <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
              No groups yet. Create one to organize your cameras.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {groups.map(g => (
                <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-dim)' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: g.color || '#00d4ff', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{g.name}</div>
                    {g.description && <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>{g.description}</div>}
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => setMode({ edit: g })} disabled={actionLoading}>Edit</button>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent-red)' }} onClick={() => handleDelete(g)} disabled={actionLoading}>Delete</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          {!mode && (
            <button className="btn btn-primary btn-sm" onClick={() => setMode('add')}>
              + Add Group
            </button>
          )}
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
